import { useEffect, useState } from "react";
import { isNativeApp } from "@/lib/native-platform";

/**
 * Modo de teste (demo) — validação de Premium e Pro no telemóvel sem custos.
 *
 * Quando activo:
 *  - nenhuma server function paga é chamada (IA, Google Places/Directions/Maps);
 *  - nenhum crédito/quota é consumido (as chamadas nem chegam ao servidor);
 *  - o utilizador é tratado como Premium/Pro apenas no dispositivo, para poder
 *    percorrer os ecrãs; nada é escrito na base de dados.
 *
 * Disponível apenas em builds internos (VITE_DEMO_MODE_ENABLED=true, como no
 * build nativo de teste) ou dentro do shell nativo. Nunca na web pública.
 */

const STORAGE_KEY = "routify_demo_mode";
const EVENT = "routify:demo-mode";

/**
 * O modo de teste só existe quando a build o ativa explicitamente. A app
 * nativa real usa o backend, a IA e os Maps verdadeiros.
 */
export function isDemoModeAvailable(): boolean {
  return import.meta.env.VITE_DEMO_MODE_ENABLED === "true";
}

export function isDemoModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (!isDemoModeAvailable()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setDemoModeEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (enabled) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useDemoMode(): { available: boolean; enabled: boolean; setEnabled: (v: boolean) => void } {
  const [enabled, setEnabled] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const sync = () => {
      setAvailable(isDemoModeAvailable());
      setEnabled(isDemoModeEnabled());
    };
    sync();
    window.addEventListener(EVENT, sync);
    return () => window.removeEventListener(EVENT, sync);
  }, []);

  return { available, enabled, setEnabled: setDemoModeEnabled };
}

/**
 * Envolve uma server function paga: em modo de teste devolve dados locais e
 * nunca faz a chamada real.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function demoFn<F extends (...args: any[]) => Promise<any>>(
  real: F,
  fake: (...args: Parameters<F>) => Awaited<ReturnType<F>> | Promise<Awaited<ReturnType<F>>>,
): F {
  return (async (...args: Parameters<F>) => {
    if (isDemoModeEnabled()) return await fake(...args);
    return await real(...args);
  }) as F;
}
